import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  type ExtendProviderSessionInput,
  type ExtendProviderSessionRequest,
  type PaymentMethod,
  providerApi,
  type ProviderBooking,
  type ProviderSession,
  type SessionStatus,
  type ProviderVenue,
  type StopProviderSessionInput,
  type StartWalkInSessionInput,
  isOfflineApiError,
} from "../../lib/api";
import {
  calculateIntervalTotalAmount,
} from "../../lib/provider-pricing";
import {
  subscribeRealtimeConnection,
  subscribeRealtimeVersion,
} from "../../lib/realtime";
import {
  createOfflineRecordId,
  listCachedApiEntries,
  updateCachedApiResponses,
  writeCachedApiResponse,
} from "../../lib/offline-store";
import { useProviderOffline } from "../../lib/provider-offline";
import {
  findCachedBookingById,
  listCachedBookings,
  mergeBookingIntoCachedResponse,
} from "../provider-bookings/booking-cache";
import { useAppPreferences } from "../../lib/preferences";

function getErrorDetail(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.detail;
  }

  return fallback;
}

function getStartWalkInErrorMessage(error: unknown, t: (key: string) => string) {
  if (error instanceof ApiError) {
    if (error.detail.includes("greater than or equal to 15")) {
      return t("Walk-in duration must be at least 15 minutes.");
    }
    if (error.detail.includes("less than or equal to 720")) {
      return t("Walk-in duration cannot exceed 720 minutes.");
    }
    return error.detail;
  }

  return t("Unable to start walk-in session.");
}

interface UseProviderSessionsArgs {
  notificationSessionId?: string | null;
  applyOfflineSessionStart: (session: ProviderSession) => void;
  applyOfflineSessionUpdate: (session: ProviderSession) => void;
  applyOfflineSessionStop: (session: ProviderSession) => void;
  selectedVenue: ProviderVenue | null;
  syncOperationalState: (
    preferredVenueId?: string | null,
    preferredUnitId?: string | null,
  ) => Promise<void>;
}

function buildSessionsPath(selectedVenue: ProviderVenue | null) {
  const params = new URLSearchParams();
  if (selectedVenue?.id) {
    params.set("venue_id", selectedVenue.id);
  }
  params.set("statuses", "active");
  return `/api/v1/provider/sessions?${params.toString()}`;
}

function buildSessionsPathForStatuses(
  selectedVenue: ProviderVenue | null,
  statuses: SessionStatus[],
) {
  const params = new URLSearchParams();
  if (selectedVenue?.id) {
    params.set("venue_id", selectedVenue.id);
  }
  params.set("statuses", statuses.join(","));
  return `/api/v1/provider/sessions?${params.toString()}`;
}

function sortActiveSessions(sessions: ProviderSession[]) {
  return [...sessions].sort((left, right) =>
    right.started_at.localeCompare(left.started_at),
  );
}

function sortRecentSessions(sessions: ProviderSession[]) {
  return [...sessions].sort((left, right) => {
    const leftKey = left.ended_at ?? left.updated_at;
    const rightKey = right.ended_at ?? right.updated_at;
    return rightKey.localeCompare(leftKey);
  });
}

const RECENT_SESSION_LIMIT = 8;
const SESSION_CHECK_IN_EARLY_MINUTES = 15;
const ACTIVE_PROVIDER_BOOKING_STATUSES: ProviderBooking["status"][] = [
  "pending",
  "confirmed",
  "checked_in",
];

function getDurationMinutes(startedAt: string, endedAt: string | null, plannedEndAt: string | null) {
  const startedAtMs = new Date(startedAt).getTime();
  const endedAtMs = endedAt ? new Date(endedAt).getTime() : Date.now();
  const durationMinutes = Math.max(0, Math.floor((endedAtMs - startedAtMs) / 60000));

  if (!plannedEndAt) {
    return {
      durationMinutes,
      overtimeMinutes: 0,
      isOvertime: false,
    };
  }

  const plannedEndMs = new Date(plannedEndAt).getTime();
  const overtimeMinutes = Math.max(0, Math.floor((endedAtMs - plannedEndMs) / 60000));
  return {
    durationMinutes,
    overtimeMinutes,
    isOvertime: overtimeMinutes > 0,
  };
}

function getOfflineBookingStartError(booking: ProviderBooking, now = new Date()) {
  const scheduledStartAtMs = new Date(booking.scheduled_start_at).getTime();
  const scheduledEndAtMs = new Date(booking.scheduled_end_at).getTime();
  const earliestStartAtMs = scheduledStartAtMs - SESSION_CHECK_IN_EARLY_MINUTES * 60000;
  const nowMs = now.getTime();

  if (nowMs < earliestStartAtMs) {
    return "Cannot start this session more than 15 minutes before the booking begins.";
  }

  if (nowMs >= scheduledEndAtMs) {
    return "Cannot start a session after the booking has already ended.";
  }

  return null;
}

function getExtendedPlannedEndAt(
  session: ProviderSession,
  additionalMinutes: number,
) {
  const baseMs = session.planned_end_at
    ? new Date(session.planned_end_at).getTime()
    : Date.now();
  return new Date(baseMs + additionalMinutes * 60000).toISOString();
}

function getOfflineExtensionConflict(
  session: ProviderSession,
  nextPlannedEndAt: string,
) {
  const cachedBookings = listCachedBookings(listCachedApiEntries());

  return cachedBookings.find(
    (booking) =>
      booking.unit_id === session.unit_id &&
      booking.id !== session.booking_id &&
      ACTIVE_PROVIDER_BOOKING_STATUSES.includes(booking.status) &&
      booking.scheduled_start_at < nextPlannedEndAt &&
      booking.scheduled_end_at > session.started_at,
  );
}

export function useProviderSessions({
  notificationSessionId = null,
  applyOfflineSessionStart,
  applyOfflineSessionUpdate,
  applyOfflineSessionStop,
  selectedVenue,
  syncOperationalState,
}: UseProviderSessionsArgs) {
  const { t } = useAppPreferences();
  const { queueMutation, syncVersion } = useProviderOffline();
  const [loadedSessions, setSessions] = useState<ProviderSession[]>([]);
  const [loadedRecentSessions, setRecentSessions] = useState<ProviderSession[]>([]);
  const currentVenueIdRef = useRef(selectedVenue?.id);
  currentVenueIdRef.current = selectedVenue?.id;
  const requestRevisionRef = useRef(0);
  useEffect(() => () => { requestRevisionRef.current += 1; }, []);
  const sessions = useMemo(() => loadedSessions.filter(session => session.venue_id === selectedVenue?.id), [loadedSessions, selectedVenue?.id]);
  const recentSessions = useMemo(() => {
    const scoped = loadedRecentSessions.filter(session => session.venue_id === selectedVenue?.id);
    const recent = scoped.slice(0, RECENT_SESSION_LIMIT);
    const target = scoped.find(session => session.id === notificationSessionId);
    return target && !recent.includes(target) ? [...recent, target] : recent;
  }, [loadedRecentSessions, notificationSessionId, selectedVenue?.id]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creatingWalkIn, setCreatingWalkIn] = useState(false);
  const [startingBookingId, setStartingBookingId] = useState<string | null>(null);
  const [extendingSessionId, setExtendingSessionId] = useState<string | null>(null);
  const [stoppingSessionId, setStoppingSessionId] = useState<string | null>(null);
  const blockedExtensionSessionIdsRef = useRef(new Set<string>());
  const activeCachePath = buildSessionsPath(selectedVenue);
  const recentCachePath = buildSessionsPathForStatuses(selectedVenue, ["completed"]);

  const persistSessions = useCallback(
    (nextSessions: ProviderSession[]) => {
      if (currentVenueIdRef.current !== selectedVenue?.id) return;
      const sortedSessions = sortActiveSessions(nextSessions);
      setSessions(sortedSessions);
      writeCachedApiResponse(activeCachePath, {
        sessions: sortedSessions,
      });
    },
    [activeCachePath, selectedVenue?.id],
  );

  const persistRecentSessions = useCallback(
    (nextSessions: ProviderSession[]) => {
      if (currentVenueIdRef.current !== selectedVenue?.id) return;
      // Keep the complete response so a notification can reveal a record
      // outside the ordinary recent-eight presentation window.
      const sortedSessions = sortRecentSessions(nextSessions);
      setRecentSessions(sortedSessions);
      writeCachedApiResponse(recentCachePath, {
        sessions: sortedSessions,
      });
    },
    [recentCachePath, selectedVenue?.id],
  );

  const replaceCachedBooking = useCallback((booking: ProviderBooking) => {
    updateCachedApiResponses((path, value) =>
      mergeBookingIntoCachedResponse(path, value, booking),
    );
  }, []);

  const updateCachedBookingById = useCallback(
    (
      bookingId: string,
      updater: (booking: ProviderBooking) => ProviderBooking,
    ) => {
      const cachedBooking = findCachedBookingById(listCachedApiEntries(), bookingId);
      if (!cachedBooking) {
        return null;
      }

      const updatedBooking = updater(cachedBooking);
      replaceCachedBooking(updatedBooking);
      return updatedBooking;
    },
    [replaceCachedBooking],
  );

  const startCachedSessionFromBooking = useCallback(
    (booking: ProviderBooking, note: string) => {
      const bookingStartError = getOfflineBookingStartError(booking);
      if (bookingStartError) {
        setError(t(bookingStartError));
        return null;
      }

      const session: ProviderSession = {
        id: createOfflineRecordId("session"),
        venue_id: booking.venue_id,
        venue_name: booking.venue_name,
        unit_id: booking.unit_id,
        unit_name: booking.unit_name,
        booking_id: booking.id,
        customer_name: booking.customer_name,
        customer_email: booking.customer_email,
        customer_phone: booking.customer_phone,
        source: "booking",
        status: "active",
        started_at: new Date().toISOString(),
        planned_end_at: booking.scheduled_end_at,
        ended_at: null,
        elapsed_total_amount: 0,
        planned_total_amount: booking.scheduled_total_amount,
        payment_collected: false,
        payment_method: null,
        payment_recorded_at: null,
        payment_note: null,
        notes: note || booking.notes || null,
        ended_note: null,
        duration_minutes: 0,
        overtime_minutes: 0,
        is_overtime: false,
        notification_revision: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      persistSessions([session, ...sessions]);
      applyOfflineSessionStart(session);
      replaceCachedBooking({
        ...booking,
        status: "checked_in",
        status_note: note.trim().length > 0 ? note : booking.status_note,
        updated_at: session.updated_at,
      });
      queueMutation({
        method: "POST",
        path: `/api/v1/provider/bookings/${booking.id}/session`,
        body: {
          note: note || null,
        },
        label: `Start session for ${booking.customer_name}`,
        local_resource_id: session.id,
      });
      setNotice(
        t("Started session for {name} offline. It will sync when connection returns.", { name: booking.customer_name }),
      );
      return session;
    },
    [applyOfflineSessionStart, persistSessions, queueMutation, replaceCachedBooking, sessions, t],
  );

  const reloadSessions = useCallback(async () => {
    if (currentVenueIdRef.current !== selectedVenue?.id) return false;
    const revision = ++requestRevisionRef.current;
    const isCurrent = () => revision === requestRevisionRef.current && currentVenueIdRef.current === selectedVenue?.id;
    if (!selectedVenue) {
      setSessions([]);
      setRecentSessions([]);
      setError(null);
      setIsLoading(false);
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [activeResponse, completedResponse] = await Promise.allSettled([
        providerApi.listSessions({
          venueId: selectedVenue.id,
          statuses: ["active"],
        }),
        providerApi.listSessions({
          venueId: selectedVenue.id,
          statuses: ["completed"],
        }),
      ]);
      if (!isCurrent()) return false;
      if (activeResponse.status === "fulfilled") {
        persistSessions(activeResponse.value.sessions);
        blockedExtensionSessionIdsRef.current.clear();
      } else {
        setError(getErrorDetail(activeResponse.reason, t("Unable to load live sessions.")));
      }

      if (completedResponse.status === "fulfilled") {
        persistRecentSessions(completedResponse.value.sessions);
      } else {
        setRecentSessions([]);
      }

      return activeResponse.status === "fulfilled";
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [persistRecentSessions, persistSessions, selectedVenue, t]);

  const reload = useCallback(async () => {
    await reloadSessions();
  }, [reloadSessions]);

  useEffect(() => {
    setNotice(null);
    void reload();
  }, [reload]);

  useEffect(() => {
    if (syncVersion === 0) {
      return;
    }

    void reload();
  }, [reload, syncVersion]);

  // Reload when realtime push arrives.
  useEffect(() => {
    return subscribeRealtimeVersion(() => {
      void reload();
    });
  }, [reload]);

  useEffect(() => {
    return subscribeRealtimeConnection((isConnected) => {
      if (isConnected) {
        void reload();
      }
    });
  }, [reload]);

  // Polling remains a reconciliation safety net while SSE is connected.
  useEffect(() => {
    if (!selectedVenue) {
      return;
    }

    const intervalId = setInterval(() => {
      void reload();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [reload, selectedVenue]);

  const startSessionFromBooking = useCallback(
    async (booking: ProviderBooking, note: string) => {
      setStartingBookingId(booking.id);
      setError(null);
      setNotice(null);

      try {
        const session = await providerApi.startSessionFromBooking(booking.id, {
          note: note || null,
        });
        await syncOperationalState(session.venue_id, session.unit_id);
        await reload();
        setNotice(t("Started session for {name}.", { name: session.customer_name }));
        return session;
      } catch (requestError) {
        if (isOfflineApiError(requestError) && selectedVenue) {
          return startCachedSessionFromBooking(booking, note);
        }

        setError(getErrorDetail(requestError, t("Unable to start session.")));
        return null;
      } finally {
        setStartingBookingId(null);
      }
    },
    [
      applyOfflineSessionStart,
      persistSessions,
      queueMutation,
      reload,
      selectedVenue,
      startCachedSessionFromBooking,
      syncOperationalState,
      t,
    ],
  );

  const startWalkInSession = useCallback(
    async (payload: StartWalkInSessionInput) => {
      setCreatingWalkIn(true);
      setError(null);
      setNotice(null);

      try {
        const session = await providerApi.startWalkInSession(payload);
        await syncOperationalState(session.venue_id, session.unit_id);
        await reload();
        setNotice(t("Started walk-in session for {name}.", { name: session.customer_name }));
        return session;
      } catch (requestError) {
        if (isOfflineApiError(requestError) && selectedVenue) {
          const unit = selectedVenue.units.find((value) => value.id === payload.unit_id);
          const startedAt = new Date().toISOString();
          const plannedEndAt = new Date(
            Date.now() + payload.planned_duration_minutes * 60000,
          ).toISOString();
          const session: ProviderSession = {
            id: createOfflineRecordId("session"),
            venue_id: selectedVenue.id,
            venue_name: selectedVenue.name,
            unit_id: payload.unit_id,
            unit_name: unit?.name ?? t("Unit"),
            booking_id: null,
            customer_name: payload.customer_name?.trim() || t("Walk-in guest"),
            customer_email: payload.customer_email ?? null,
            customer_phone: payload.customer_phone ?? null,
            source: "walk_in",
            status: "active",
            started_at: startedAt,
            planned_end_at: plannedEndAt,
            ended_at: null,
            elapsed_total_amount: 0,
            planned_total_amount: unit
              ? calculateIntervalTotalAmount(
                  unit,
                  selectedVenue.timezone,
                  startedAt,
                  plannedEndAt,
                )
              : 0,
            payment_collected: false,
            payment_method: null,
            payment_recorded_at: null,
            payment_note: null,
            notes: payload.notes ?? null,
            ended_note: null,
            duration_minutes: 0,
            overtime_minutes: 0,
            is_overtime: false,
            notification_revision: 0,
            created_at: startedAt,
            updated_at: startedAt,
          };
          persistSessions([session, ...sessions]);
          applyOfflineSessionStart(session);
          queueMutation({
            method: "POST",
            path: "/api/v1/provider/sessions/walk-in",
            body: payload,
            label: `Start walk-in for ${session.customer_name}`,
            local_resource_id: session.id,
          });
          setNotice(
            t("Started walk-in session for {name} offline. It will sync when connection returns.", { name: session.customer_name }),
          );
          return session;
        }

        setError(getStartWalkInErrorMessage(requestError, t));
        return null;
      } finally {
        setCreatingWalkIn(false);
      }
    },
    [
      applyOfflineSessionStart,
      persistSessions,
      queueMutation,
      reload,
      selectedVenue,
      sessions,
      syncOperationalState,
      t,
    ],
  );

  const extendSession = useCallback(
    async (sessionId: string, payload: ExtendProviderSessionInput) => {
      setExtendingSessionId(sessionId);
      setError(null);
      setNotice(null);

      if (blockedExtensionSessionIdsRef.current.has(sessionId)) {
        setError(t("Session refresh failed. Reload live sessions before extending again."));
        setExtendingSessionId(null);
        return null;
      }

      const currentSession = sessions.find((value) => value.id === sessionId) ?? null;
      if (!currentSession) {
        setError(t("This session is not cached for extension."));
        setExtendingSessionId(null);
        return null;
      }

      const request: ExtendProviderSessionRequest = {
        additional_minutes: payload.additional_minutes,
        expected_notification_revision: currentSession.notification_revision,
      };

      try {
        const session = await providerApi.extendSession(sessionId, request);
        await syncOperationalState(session.venue_id, session.unit_id);
        await reload();
        setNotice(
          t("Extended session for {name} by {minutes} minutes.", { name: session.customer_name, minutes: payload.additional_minutes }),
        );
        return session;
      } catch (requestError) {
        if (
          requestError instanceof ApiError &&
          requestError.status === 409 &&
          requestError.detail === "stale_session_revision"
        ) {
          const activeSessionsReloaded = await reloadSessions();
          if (activeSessionsReloaded) {
            setError(t("Session changed elsewhere. Reloaded current session."));
          } else {
            blockedExtensionSessionIdsRef.current.add(sessionId);
            setError(t("Session refresh failed. Reload live sessions before extending again."));
          }
          return null;
        }

        if (isOfflineApiError(requestError)) {
          const unit = selectedVenue?.units.find((value) => value.id === currentSession.unit_id) ?? null;

          const nextPlannedEndAt = getExtendedPlannedEndAt(
            currentSession,
            payload.additional_minutes,
          );
          const conflictingBooking = getOfflineExtensionConflict(
            currentSession,
            nextPlannedEndAt,
          );
          if (conflictingBooking) {
            setError(t("This extension overlaps an existing booking on the selected unit."));
            return null;
          }

          const sessionDuration = getDurationMinutes(
            currentSession.started_at,
            null,
            nextPlannedEndAt,
          );
          const updatedSession: ProviderSession = {
            ...currentSession,
            planned_end_at: nextPlannedEndAt,
            planned_total_amount: unit
              ? calculateIntervalTotalAmount(
                  unit,
                  selectedVenue?.timezone ?? "UTC",
                  currentSession.started_at,
                  nextPlannedEndAt,
                )
              : currentSession.planned_total_amount,
            overtime_minutes: sessionDuration.overtimeMinutes,
            is_overtime: sessionDuration.isOvertime,
            notification_revision: currentSession.notification_revision + 1,
            updated_at: new Date().toISOString(),
          };
          persistSessions(
            sessions.map((value) => (value.id === sessionId ? updatedSession : value)),
          );
          applyOfflineSessionUpdate(updatedSession);
          if (currentSession.booking_id) {
            updateCachedBookingById(currentSession.booking_id, (booking) => ({
              ...booking,
              scheduled_end_at: nextPlannedEndAt,
              scheduled_total_amount: unit
                ? calculateIntervalTotalAmount(
                    unit,
                    booking.venue_timezone,
                    booking.scheduled_start_at,
                    nextPlannedEndAt,
                  )
                : booking.scheduled_total_amount,
              updated_at: updatedSession.updated_at,
            }));
          }
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/sessions/${sessionId}/extend`,
            body: request,
            label: `Extend session for ${currentSession.customer_name}`,
          });
          setNotice(
            t("Extended session for {name} offline. It will sync when connection returns.", { name: currentSession.customer_name }),
          );
          return updatedSession;
        }

        setError(getErrorDetail(requestError, t("Unable to extend session.")));
        return null;
      } finally {
        setExtendingSessionId(null);
      }
    },
    [
      applyOfflineSessionUpdate,
      persistSessions,
      queueMutation,
      reload,
      reloadSessions,
      sessions,
      syncOperationalState,
      selectedVenue?.timezone,
      t,
      updateCachedBookingById,
    ],
  );

  const stopSession = useCallback(
    async (sessionId: string, payload: StopProviderSessionInput = {}) => {
      setStoppingSessionId(sessionId);
      setError(null);
      setNotice(null);

      try {
        const session = await providerApi.stopSession(sessionId, {
          note: payload.note || null,
          payment_collected: payload.payment_collected ?? false,
          payment_method: payload.payment_method ?? null,
          payment_note: payload.payment_note || null,
        });
        await syncOperationalState(session.venue_id, session.unit_id);
        await reload();
        setNotice(
          session.payment_collected
            ? t("Stopped session for {name} and recorded payment.", { name: session.customer_name })
            : t("Stopped session for {name}.", { name: session.customer_name }),
        );
        return session;
      } catch (requestError) {
        if (isOfflineApiError(requestError)) {
          const currentSession = sessions.find((value) => value.id === sessionId) ?? null;
          if (!currentSession) {
            setError(t("This session is not cached for offline stop."));
            return null;
          }

          const unit = selectedVenue?.units.find((value) => value.id === currentSession.unit_id) ?? null;

          const endedAt = new Date().toISOString();
          const duration = getDurationMinutes(
            currentSession.started_at,
            endedAt,
            currentSession.planned_end_at,
          );
          const session: ProviderSession = {
            ...currentSession,
            status: "completed",
            ended_at: endedAt,
            elapsed_total_amount: unit
              ? calculateIntervalTotalAmount(
                  unit,
                  selectedVenue?.timezone ?? "UTC",
                  currentSession.started_at,
                  endedAt,
                )
              : currentSession.elapsed_total_amount,
            payment_collected: payload.payment_collected ?? false,
            payment_method: payload.payment_method ?? null,
            payment_recorded_at:
              payload.payment_collected ? endedAt : null,
            payment_note:
              payload.payment_collected && payload.payment_note
                ? payload.payment_note
                : null,
            ended_note: payload.note || null,
            duration_minutes: duration.durationMinutes,
            overtime_minutes: duration.overtimeMinutes,
            is_overtime: duration.isOvertime,
            updated_at: endedAt,
          };
          persistSessions(sessions.filter((value) => value.id !== sessionId));
          persistRecentSessions([session, ...recentSessions]);
          applyOfflineSessionStop(session);
          if (currentSession.booking_id) {
            updateCachedBookingById(currentSession.booking_id, (booking) => ({
              ...booking,
              status: "completed",
              status_note:
                payload.note && payload.note.trim().length > 0
                  ? payload.note
                  : booking.status_note,
              updated_at: endedAt,
            }));
          }
          queueMutation({
            method: "PUT",
            path: `/api/v1/provider/sessions/${sessionId}/stop`,
            body: {
              note: payload.note || null,
              payment_collected: payload.payment_collected ?? false,
              payment_method: payload.payment_method ?? null,
              payment_note: payload.payment_note || null,
            },
            label: `Stop session for ${currentSession.customer_name}`,
          });
          setNotice(
            payload.payment_collected
              ? t("Stopped session for {name} offline and queued payment capture for sync.", { name: currentSession.customer_name })
              : t("Stopped session for {name} offline. It will sync when connection returns.", { name: currentSession.customer_name }),
          );
          return session;
        }

        setError(getErrorDetail(requestError, t("Unable to stop session.")));
        return null;
      } finally {
        setStoppingSessionId(null);
      }
    },
    [
      applyOfflineSessionStop,
      persistSessions,
      persistRecentSessions,
      queueMutation,
      recentSessions,
      reload,
      sessions,
      syncOperationalState,
      selectedVenue?.timezone,
      t,
      updateCachedBookingById,
    ],
  );

  const activeSessionsByBookingId = useMemo(
    () =>
      Object.fromEntries(
        sessions
          .filter((session) => Boolean(session.booking_id))
          .map((session) => [session.booking_id as string, session]),
      ) as Record<string, ProviderSession>,
    [sessions],
  );

  return {
    sessions,
    recentSessions,
    activeSessionsByBookingId,
    isLoading,
    error,
    notice,
    creatingWalkIn,
    startingBookingId,
    extendingSessionId,
    stoppingSessionId,
    startSessionFromBooking,
    startWalkInSession,
    extendSession,
    stopSession,
    reload,
  };
}
